/**
 * React hook for tracking PostHog events
 */

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { trackEvent as trackEventAction, PosthogEventProperties } from '~/services/posthog';
import { validateEventProperties } from '~/utils/posthog/validators';
import { sanitizePayload } from '~/utils/posthog/sanitizers';

/**
 * Hook for tracking events through Redux
 */
export const useTrackEvent = () => {
  const dispatch = useDispatch();

  const trackEvent = useCallback(
    (eventName: string, properties?: PosthogEventProperties) => {
      // Sanitize and validate properties
      const sanitized = properties ? sanitizePayload(properties) : undefined;
      const validated = validateEventProperties(sanitized);

      // Dispatch the action
      dispatch(trackEventAction(eventName, validated));
    },
    [dispatch]
  );

  return trackEvent;
};

/**
 * Hook for tracking errors
 */
export const useTrackError = () => {
  const trackEvent = useTrackEvent();

  return useCallback(
    (error: Error | string, context?: Record<string, any>) => {
      const errorMessage = error instanceof Error ? error.message : error;
      const errorStack = error instanceof Error ? error.stack : undefined;

      trackEvent('app:error:client_error', {
        errorMessage,
        errorStack,
        ...context,
      });
    },
    [trackEvent]
  );
};